import toast from "react-hot-toast";
import { getTenantConfig } from "../services/api";
import { setMunicipio } from "../services/data";
import { clear } from "../services/db";

type Tokens = { xAuthToken?: string; xTnToken?: string };

const TOKEN_KEYS = {
  auth: "x-auth-token",
  tn: "x-tn-token",
} as const;

const STORAGE_KEYS = {
  auth: "xAuthToken",
  tn: "xTnToken",
  errors: "errors",
  textareaSize: "textareaSize",
} as const;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function decodeUpTo(value: string, maxDepth = 3): string {
  let current = value;
  for (let i = 0; i < maxDepth; i++) {
    const next = safeDecode(current);
    if (next === current) return current;
    current = next;
  }
  return current;
}

function readTokens(params: URLSearchParams): Tokens {
  const xAuthToken = params.get(TOKEN_KEYS.auth) || undefined;
  const xTnToken = params.get(TOKEN_KEYS.tn) || undefined;
  return { xAuthToken, xTnToken };
}

function coalesceTokens(primary: Tokens, fallback: Tokens): Tokens {
  return {
    xAuthToken: primary.xAuthToken ?? fallback.xAuthToken,
    xTnToken: primary.xTnToken ?? fallback.xTnToken,
  };
}

/**
 * Tenta interpretar `raw` como URL; se falhar, tenta como querystring.
 */
function parseMaybeUrl(raw: string, base: string): URL | null {
  try {
    return new URL(raw, base);
  } catch {
    const qs = raw.startsWith("?") ? raw : `?${raw}`;
    try {
      return new URL(`/${qs}`, base);
    } catch {
      return null;
    }
  }
}

/**
 * Extrai tokens do search "top-level" e também de callback aninhado (com decode e depth limit).
 */
function extractTokensFromCurrentUrl(href: string, origin: string, maxCallbackDepth = 6): Tokens {
  const url = new URL(href);
  let tokens = readTokens(url.searchParams);

  let callback = url.searchParams.get("callback") || "";
  for (let depth = 0; depth < maxCallbackDepth && callback; depth++) {
    const decoded = decodeUpTo(callback, 3);
    const callbackUrl = parseMaybeUrl(decoded, origin);

    if (!callbackUrl) break;

    tokens = coalesceTokens(tokens, readTokens(callbackUrl.searchParams));
    callback = callbackUrl.searchParams.get("callback") || "";
  }

  return tokens;
}

function persistTokens(tokens: Tokens) {
  if (tokens.xAuthToken) localStorage.setItem(STORAGE_KEYS.auth, tokens.xAuthToken);
  if (tokens.xTnToken) localStorage.setItem(STORAGE_KEYS.tn, tokens.xTnToken);
}

function preserveTextareaSize(): string | null {
  return localStorage.getItem(STORAGE_KEYS.textareaSize);
}

function restoreTextareaSize(value: string | null) {
  if (value) localStorage.setItem(STORAGE_KEYS.textareaSize, value);
}

export async function checkAutenticationByUrl(): Promise<boolean> {
  const origin = window.location.origin;
  const href = window.location.href;

  const prevTextareaSize = preserveTextareaSize();

  const tokens = extractTokensFromCurrentUrl(href, origin);
  const hasAnyToken = Boolean(tokens.xAuthToken || tokens.xTnToken);

  if (!hasAnyToken) return false;

  await clear();
  restoreTextareaSize(prevTextareaSize);

  persistTokens(tokens);

  const projectInfo = await getTenantConfig();

  if (!projectInfo) {
    toast.error("Não foi possível obter as configurações de projeto");
    return true;
  }

  setMunicipio(projectInfo.cidade);
  localStorage.setItem(STORAGE_KEYS.errors, "{}");

  return true;
}